/**
 * Created by Jerker Nord on 2021-05-04.
 */
import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../../core/winpresentation/SimplePresentationRunner";
import {TheBorder} from "../../TheBorder";
import {TheBorderGameMode} from "../../server/TheBorderGameMode";
import {gsap, TimelineMax} from "gsap";
import {IBetClusterWin, IComboWinData} from "../../server/TheBorderData";
import {TheBorderStatics} from "../../TheBorderStatics";
import {ISymbol} from "../../../core/reel/ReelInterfaces";
import {TheBorderSymbol} from "../../reels/TheBorderSymbol";
import {TheBorderSound} from "../../sound/TheBorderSound";

export class TheBorderAvalancheWinRunner implements SimplePresentation {

    /**
     * Removes win symbols and trigger collapse reel animation on all reels
     */

    private _collapseDuration: number;
    private _mainTL: gsap.core.Timeline;
    private _xBombPositions: ISymbol[];

    private static parseXBombPositions(reels: ISymbol[][]): ISymbol[] {
        let xBombPositions: ISymbol[] = [];
        for (let i: number = 0; i < reels.length; i++) {
            for (let j: number = 0; j < reels[i].length; j++) {
                if (reels[i][j].symbolName.indexOf("X_BOMB") > -1) {
                    xBombPositions.push(reels[i][j]);
                }
            }
        }
        return xBombPositions;
    }

    private static parseXBombExplodePositions(xBombPositions: ISymbol[]): boolean[][][] {
        let allXBombExplodePositions: boolean[][][] = [];
        let reel: number;
        let row: number;
        let xBombYPos: number;
        let xBombAtTop: boolean;
        let xBombAtBottom: boolean;
        let xBombSize: number;
        let xBombSplitYPosPadding: number;
        for (let i: number = 0; i < xBombPositions.length; i++) {
            reel = xBombPositions[i].reelId;
            row = xBombPositions[i].reelPosition - 1;
            xBombAtTop = false;
            xBombAtBottom = false;
            xBombYPos = TheBorder.allReelsView.getSymbolAtPosition(reel, row).position.y;
            xBombSplitYPosPadding = 0;
            xBombSize = TheBorder.allReelsView.getSymbolAtPosition(reel, row).sizeMultiplier;

            let xBombExplodePositions: boolean[][] = [];
            for (let j: number = 0; j < 6; j++) {
                let reel: boolean[] = [];
                for (let k: number = 0; k < TheBorder.allReelsView.getReelLength(j); k++) {
                    reel.push(false);
                }
                xBombExplodePositions.push(reel);
            }

            //Remove xBomb
            xBombExplodePositions[reel][row] = true;
            if (row > 0) {
                //Remove symbol above XBomb
                const symName: string = TheBorder.allReelsView.getSymbolAtPosition(reel, row - 1).symbolName;
                if ((TheBorder.SYMBOLS_NOT_EXPLODING.indexOf(symName) === -1) && (symName.indexOf("X_SPLIT") === -1)) {
                    xBombExplodePositions[reel][row - 1] = true;
                }
            } else {
                xBombAtTop = true;
            }
            if (row < (TheBorder.allReelsView.getReelLength(reel) - 1)) {
                //Remove symbol below XBomb
                const symName: string = TheBorder.allReelsView.getSymbolAtPosition(reel, row + 1).symbolName;
                if (TheBorder.SYMBOLS_NOT_EXPLODING.indexOf(symName) === -1 && (symName.indexOf("X_SPLIT") === -1)) {
                    xBombExplodePositions[reel][row + 1] = true;
                }
            } else {
                xBombAtBottom = true;
            }

            //Find top and bottom right symbol
            if (reel < 5) {
                let topRightSymbol: ISymbol;
                if (!xBombAtTop) {
                    let topSymbolsRightOfXBomb: ISymbol[] = TheBorder.allReelsView.mainReelModels[reel + 1].reelView.getSymbolsClosestToYPos(xBombYPos, 100);
                    if (topSymbolsRightOfXBomb[0].position.y == xBombYPos) {
                        topRightSymbol = TheBorder.allReelsView.getSymbolAtPosition(topSymbolsRightOfXBomb[0].reelId, topSymbolsRightOfXBomb[0].reelPosition - 2);
                    } else {
                        topRightSymbol = topSymbolsRightOfXBomb[0];
                    }
                } else {
                    topRightSymbol = TheBorder.allReelsView.getSymbolAtPosition(reel + 1, row);
                }

                let bottomRightSymbol: ISymbol;
                if (!xBombAtBottom) {
                    bottomRightSymbol = TheBorder.allReelsView.mainReelModels[reel + 1].reelView.getSymbolsClosestToYPos(xBombYPos + (TheBorderStatics.symbolHeight * xBombSize), 100 * xBombSize)[0];
                } else {
                    bottomRightSymbol = TheBorder.allReelsView.mainReelModels[reel + 1].reelView.getSymbol((TheBorder.allReelsView.getReelLength(reel + 1) - 1));
                }

                //Remove all symbols to the right of xBomb
                for (let k = (topRightSymbol.reelPosition - 1); k < (bottomRightSymbol.reelPosition); k++) {
                    const symName: string = TheBorder.allReelsView.getSymbolAtPosition(reel + 1, k).symbolName;
                    if ((TheBorder.SYMBOLS_NOT_EXPLODING.indexOf(symName) === -1) && (symName.indexOf("X_SPLIT") === -1)) {
                        xBombExplodePositions[reel + 1][k] = true;
                    }
                }
            }

            if (reel > 0) {
                //Find top and bottom left symbol
                let topLeftSymbol: ISymbol;
                if (!xBombAtTop) {
                    let topSymbolsLeftOfXBomb: ISymbol[] = TheBorder.allReelsView.mainReelModels[reel - 1].reelView.getSymbolsClosestToYPos(xBombYPos, 50);
                    if (topSymbolsLeftOfXBomb[0].position.y == xBombYPos) {
                        topLeftSymbol = TheBorder.allReelsView.getSymbolAtPosition(topSymbolsLeftOfXBomb[0].reelId, topSymbolsLeftOfXBomb[0].reelPosition - 2);
                    } else {
                        topLeftSymbol = topSymbolsLeftOfXBomb[0];
                    }
                } else {
                    topLeftSymbol = TheBorder.allReelsView.getSymbolAtPosition(reel - 1, row);
                }

                let bottomLeftSymbol: ISymbol
                if (!xBombAtBottom) {
                    bottomLeftSymbol = TheBorder.allReelsView.mainReelModels[reel - 1].reelView.getSymbolsClosestToYPos(xBombYPos + (TheBorderStatics.symbolHeight * xBombSize), 100 * xBombSize)[0];
                } else {
                    bottomLeftSymbol = TheBorder.allReelsView.mainReelModels[reel - 1].reelView.getSymbol(TheBorder.allReelsView.getReelLength(reel - 1) - 1);
                }

                //Remove all symbols to the left of xBomb
                for (let k = (topLeftSymbol.reelPosition - 1); k < (bottomLeftSymbol.reelPosition); k++) {
                    const symName: string = TheBorder.allReelsView.getSymbolAtPosition(reel - 1, k).symbolName;
                    if ((TheBorder.SYMBOLS_NOT_EXPLODING.indexOf(symName) === -1) && (symName.indexOf("X_SPLIT") === -1)) {
                        xBombExplodePositions[reel - 1][k] = true;
                    }
                }
            }

            allXBombExplodePositions.push(xBombExplodePositions);
        }
        return allXBombExplodePositions;
    }

    public shouldRun(): boolean {
        return (TheBorder.model.gameData.nextMode == TheBorderGameMode.NORMAL_AVALANCHE
            || TheBorder.model.gameData.nextMode == TheBorderGameMode.FREESPIN_AVALANCHE
            || TheBorder.model.gameData.nextMode == TheBorderGameMode.SUPER_FREESPIN_AVALANCHE);
    }

    public start(controller: SimplePresentationRunnerController): void {
        this._xBombPositions = TheBorderAvalancheWinRunner.parseXBombPositions(TheBorder.allReelsView.getAllReels());
        const xBombExplodePos: boolean[][][] = TheBorderAvalancheWinRunner.parseXBombExplodePositions(this._xBombPositions);

        let comboWinData: IComboWinData[] = [];
        for (let i: number = 0; i < TheBorder.model.comboWinData.length; i++) {
            comboWinData.push({
                amount: TheBorder.model.comboWinData[i].amount,
                reelPosition: TheBorder.model.comboWinData[i].reelPosition
            });
        }
        let accumulatedRoundWin: number = TheBorder.model.data.accumulatedRoundWin;
        let nextMode: string = TheBorder.model.gameData.nextMode;
        let betClusterWins: IBetClusterWin[] = [];
        for (let i: number = 0; i < TheBorder.model.betClusterWins.length; i++) {
            betClusterWins.push({
                reelWinPositions: TheBorder.model.betClusterWins[i].reelWinPositions,
                multipliedAmount: TheBorder.model.betClusterWins[i].multipliedAmount,
                symbol: TheBorder.model.betClusterWins[i].symbol
            });
        }

        this._mainTL = gsap.timeline({
            onComplete: () => {
                controller.finish();
            }
        });

        const allWinClusters: PIXI.Point[][] = TheBorder.model.allWinClusters,
            explodePositions: boolean[][] = TheBorder.model.explodePositions;
        let subClusterWinTLs: gsap.core.Timeline[] = [];
        if (allWinClusters.length > 0) {
            for (let i: number = 0; i < allWinClusters.length; i++) {
                for (let j: number = 0; j < allWinClusters[i].length; j++) {
                    this._mainTL.add((<TheBorderSymbol>TheBorder.allReelsView.getSymbolAtPosition(allWinClusters[i][j].x, allWinClusters[i][j].y)).getNoWinEndAnimation(), 0);
                }
            }

            for (let i: number = 0; i < allWinClusters.length; i++) {
                subClusterWinTLs.push(TheBorder.clusterWinView.getClusterWinAnimation(allWinClusters[i]));
            }
            subClusterWinTLs.push(gsap.timeline().add(() => {
                //TheBorder.soundPlayer.playSound(TheBorderSound.WIN_FRAMES_EXPLODE.next()); //#190
            }));
        }

        subClusterWinTLs.push(TheBorder.clusterWinView.getDarkDownNoWinAnimation(explodePositions, xBombExplodePos));

        this._mainTL.add([
            gsap.timeline().add(subClusterWinTLs),
            // #107
            () => {
                controller.setSkipCallback(() => this.skip());
            }
        ]);

        let xBombExplodeTiming: number = 1.1;
        //Different timing if it's only xBombs and no win
        if (betClusterWins.length == 0) {
            xBombExplodeTiming = 0.1;
        }
        this._mainTL.add(TheBorder.xBombView.getXBombExplodeAnimation(this._xBombPositions, explodePositions), "-=" + xBombExplodeTiming);

        // #365
        if (betClusterWins.length === 0) {
            this._mainTL.add(() => {
                TheBorder.clusterWinView.removeTape();
            }, "-=0.3");
        }

        this._mainTL.add(() => {
            TheBorder.soundPlayer.playSound(TheBorderSound.WIN_FRAMES_EXPLODE.next());
        }, "-=1.95");
        this._mainTL.add(TheBorder.clusterWinView.getComboWinAmountAnimations(comboWinData), "-=0.6");
        let darkDownStart: number = 0.9;
        //Different timing if it's only xBombs and no win
        if (betClusterWins.length == 0) {
            darkDownStart = 0;
        }
        this._mainTL.add(() => {
            TheBorder.clusterWinView.getDarkDownNoWinEndAnimation(explodePositions);
        }, "-=" + darkDownStart);

        if (nextMode !== TheBorderGameMode.NORMAL) {
            this._mainTL.add(() => TheBorder.keypadPlugin.setWin(accumulatedRoundWin, true, TheBorder.isWinBelowStake()), "-=0.7"); //#248, 249
        }

        let collapseOverlap = TheBorderStatics.COLLAPSE_OVERLAP;
        //Different timing if it's only xBombs and no win
        if (betClusterWins.length == 0) {
            collapseOverlap = 0;
        }
        this._mainTL.add(() => this.collapseReels(), "-=" + collapseOverlap);

        if (this._mainTL.duration() == 0) {
            this._mainTL.add(gsap.to(this, {duration: 0.001}));
        }
    }

    private collapseReels(): void {
        const mainTL: TimelineMax = gsap.timeline({onComplete: () => this.collapseReelsComplete()});
        //Collapse main reels
        const subCollapseReelsTLs: gsap.core.Timeline[] = [];
        for (let i: number = 0; i < TheBorder.model.allWinPositions.length; i++) {
            const collapseReelTL: gsap.core.Timeline = gsap.timeline();
            collapseReelTL.add(TheBorder.allReelsView.mainReelModels[i].collapseReel());
            subCollapseReelsTLs.push(collapseReelTL);
        }

        mainTL.add(() => {
            subCollapseReelsTLs
        });

        this._collapseDuration = 0.15;
        for (let i: number = 0; i < subCollapseReelsTLs.length; i++) {
            if (subCollapseReelsTLs[i].totalDuration() > this._collapseDuration) {
                this._collapseDuration = subCollapseReelsTLs[i].totalDuration();
            }
        }

        if (mainTL.duration() == 0) {
            mainTL.add(gsap.to(this, {duration: 0.001}));
        }
    }

    private collapseReelsComplete(): void {
        const waitingForCollapseTLs: gsap.core.Timeline = gsap.timeline({delay: this._collapseDuration});
        waitingForCollapseTLs.add(() => {
            TheBorder.model.avalancheCollapseComplete = true
        });
        waitingForCollapseTLs.add(() => TheBorder.avalancheFillUpReels.checkToStartFillUp());
    }

    private skip(): void {
        if (this._mainTL) {
            this._mainTL.totalProgress(1);

            // #411
            TheBorderSound.WIN_FRAMES_EXPLODE.list.forEach((key: string) => {
                TheBorder.soundPlayer.fadeOutEffect({
                    name: key,
                    to: 0,
                    duration: 50
                });
            });
            TheBorder.soundPlayer.playSound(TheBorderSound.WIN_FRAMES_EXPLODE_ABORT.next());
        }
    }
}